import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {ReactQueryDevtools} from "@tanstack/react-query-devtools";
import {ConfigProvider} from "antd";
import locale from "antd/locale/es_ES";
import PropTypes from "prop-types";
import {lazy, Suspense, useEffect, useState} from "react";
import {HelmetProvider} from "react-helmet-async";
import {useTranslation} from "react-i18next";
import {BrowserRouter, Route, Routes, useLocation, useParams} from "react-router-dom";
import DepositMethods from "./components/DepositMethods";
import BankOptions from "./components/DepositMethods/BankOptions";
// import BankOptions from "./components/BankOptions"; // Uncomment if exists
import {ErrorBoundaryRedirect} from "./components/ErrorBoundaryHandler/ErrorBoundaryRedirect";
import FloatButtonWallet from "./components/FloatButton";
import AddPagoMovilForm from "./components/Forms/AddPagoMovil";
import ChangePassword from "./components/Forms/ChangePassword";
import RecoverPassword from "./components/Forms/RecoverPassword";
import RegistrationModal from "./components/Forms/Registration/RegistrationModal";
import {LoginForm} from "./components/Login";
import BaseModal from "./components/Modals";
import InstallationPrompt from "./components/Modals/InstallationPrompt";
import NotificationPrompt from "./components/Modals/NotificationPrompt";
import PagoMovilListModal from "./components/Modals/PagoMovilListModal";
import PagoMovilRegistrationModal from "./components/Modals/PagoMovilRegistrationModal";
import UpdatePrompt from "./components/Modals/UpdatePrompt";
import MultiGameFloatingButton from "./components/MultiGameFloatingButton";
import {MobileBottomDock} from "./components/navigation/MobileBottomDock";
// Transfer functionality is temporarily disabled
import TransferSteps from "./components/TransferSteps";
import UserProfile from "./components/UserProfile";
import WithdrawalMethods from "./components/WithdrawalMethods";
import {DynamicTitleProvider} from "./hooks/useDynamicTitle";
import useGetAppConfig from "./hooks/useGetAppConfig";
import usePushNotifications from "./hooks/usePushNotifications";
import LOut from "./Layout";
import MobileLogin from "./pages/MobileLogin";
import MobileRegistration from "./pages/MobileRegistration";
import ProtectedHomePage from "./pages/ProtectedHomePage";
import DeleteAccount from "./pages/public/DeleteAccount";

import TermsOfService from "./pages/public/TOS";
import AppContext from "./providers/AppContext";
import {initializeAmplitude} from "./providers/analytics";
// import useMetaPixel from "./hooks/useMetaPixel"; // Disabled - Meta Pixel now handled by backend
import {useChangePassword} from "./store/useAccountScreens.store";
import {useAuthStore} from "./store/useAuth.store";
import {useBalanceStore} from "./store/useBalance.store";
import {useUserProfileInfo} from "./store/useUserProfileInfo.store";
import {useUserSupport} from "./store/useUserSupport.store";
import "./styles/button.css";

import {ConfEnv} from "./config/config";
import {initializeNativeBridge} from "./services/nativeBridge.service";
import {useThemeStore} from "./store/useTheme.store";
import {getDarkAntdTheme, getLightAntdTheme} from "./Theme";
import {startSessionValidation, stopSessionValidation} from "./util/sessionManager";

const Parley = lazy(() => import("./pages/private/Parley"));
const Results = lazy(() => import("./pages/private/Lottery/Games/DefaultLottery/pages/Results"));

/* Lazy-loaded route pages */
const AccountSettingsPage = lazy(() =>
  import("./pages/private/AccountSettings").then(m => ({default: m.AccountSettingsPage}))
);
const AYGProfile = lazy(() => import("./pages/private/AYGProfile").then(m => ({default: m.AYGProfile})));
const Bingo = lazy(() => import("./pages/private/Bingo"));
const ContactsNavbar = lazy(() => import("./pages/private/Contacts"));
const GamesList = lazy(() => import("./pages/private/Games"));
const CasinoList = lazy(() => import("./pages/private/Games/components/CasinoList"));
const GamingScreen = lazy(() => import("./pages/private/Games/pages/GamingScreen"));
const Horses = lazy(() => import("./pages/private/Horses"));
const LiveCasino = lazy(() => import("./pages/private/LiveCasino"));
const Lotoven = lazy(() => import("./pages/private/Lotoven"));
const Lottery = lazy(() => import("./pages/private/Lottery"));
const DefaultLottery = lazy(() => import("./pages/private/Lottery/Games/DefaultLottery"));
const MyTickets = lazy(() => import("./pages/private/Lottery/pages/MyTickets"));
const Movements = lazy(() => import("./pages/private/Movements"));
const News = lazy(() => import("./pages/private/News"));
const Article = lazy(() => import("./pages/private/News/components/Article"));
const PagoMovil = lazy(() => import("./pages/private/PagoMovil"));
const PaykuCallback = lazy(() => import("./pages/private/PaykuCallback"));
const ProviderProfile = lazy(() => import("./pages/private/ProviderProfile").then(m => ({default: m.ProviderProfile})));
const PublicProfilePage = lazy(() =>
  import("./pages/private/PublicProfile").then(m => ({default: m.PublicProfilePage}))
);
const RafflesList = lazy(() => import("./pages/private/Raffles"));
const RaffleTickets = lazy(() => import("./pages/private/Raffles/pages/Tickets"));
const ReferralProgram = lazy(() => import("./pages/private/ReferralProgram"));
const MiniJuegos = lazy(() => import("./pages/private/MiniJuegos"));
const FavoritesPage = lazy(() => import("./pages/private/Favorites").then(m => ({default: m.FavoritesPage})));
const Testimonials = lazy(() => import("./pages/public/Testimonials"));

import {SupportModal} from "./components/support/SupportModal";

const GameFeed = lazy(() => import("@pages/private/GameFeed").then(m => ({default: m.GameFeed})));

const ImageWithLoadSpinnerDevPreview = import.meta.env.DEV
  ? lazy(() => import("./pages/dev/ImageWithLoadSpinnerDevPreview"))
  : null;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

const ConfigLoader = () => {
  useGetAppConfig();
  return null;
};

const isMobile = window.innerWidth <= 600;

// Component to conditionally wrap routes with layout
const ConditionalLayout = ({children}) => {
  const location = useLocation();
  const noLayoutPaths = ["/mobile-login", "/mobile-registration", "/payku/callback"];
  const shouldShowLayout = !noLayoutPaths.includes(location.pathname);
  const hideFloatingButtons =
    location.pathname.startsWith("/provider/") ||
    location.pathname.startsWith("/game-feed") ||
    location.pathname.startsWith("/sports");

  if (!shouldShowLayout) {
    return <>{children}</>;
  }

  return (
    <LOut>
      {!hideFloatingButtons && <FloatButtonWallet />}
      {/* Global MultiGame Floating Button */}
      {!hideFloatingButtons && <MultiGameFloatingButton />}
      {children}
    </LOut>
  );
};

ConditionalLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

// Wrapper to extract gameIdString from params and pass to DefaultLottery
const LotteryGameRoute = () => {
  const {gameIdString} = useParams();
  return <DefaultLottery gameIdString={gameIdString} />;
};

const ScrollToTop = () => {
  const {pathname} = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const App = () => {
  const {t} = useTranslation();
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);

  const [depositModalType, setDepositModalType] = useState("depositMethods");
  const [depositModalWidth, setDepositModalWidth] = useState("678px");

  // Dynamic withdrawal modal width
  const [withdrawalModalWidth, setWithdrawalModalWidth] = useState("678px");

  const validateSession = useAuthStore(state => state.validateSession);
  const authStatus = useAuthStore(state => state.authStatus);
  const visible = useAuthStore(state => state.showAuthModal);
  const authForm = useAuthStore(state => state.authForm);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const bankAccountModalVisible = useBalanceStore(state => state.bankAccountModalVisible);
  const handleBankAccountModal = useBalanceStore(state => state.handleBankAccountModal);

  // Reset deposit flow to initial screen when modal closes
  useEffect(() => {
    if (!bankAccountModalVisible) {
      setDepositModalType("depositMethods");
      setDepositModalWidth("678px");
    }
  }, [bankAccountModalVisible]);

  const withdrawalModalVisible = useBalanceStore(state => state.withdrawalModalVisible);
  const handleWithdrawalModal = useBalanceStore(state => state.handleWithdrawalModal);

  const transferModalVisible = useBalanceStore(state => state.transferModalVisible);
  const handleTransferModal = useBalanceStore(state => state.handleTransferModal);

  const addPagoMovilModalVisible = useBalanceStore(state => state.addPagoMovilModalVisible);
  const handleAddPagoMovilModal = useBalanceStore(state => state.handleAddPagoMovilModal);

  const handleChangePasswordModal = useChangePassword(state => state.handleChangePasswordModal);
  const changePasswordModalVisible = useChangePassword(state => state.changePasswordModalVisible);
  const userProfileModaVisible = useUserProfileInfo(state => state.userProfileModaVisible);
  const handleProfileModal = useUserProfileInfo(state => state.handleProfileModal);

  const userSupportModaVisible = useUserSupport(state => state.userSupportModaVisible);

  const handleSupportModal = useUserSupport(state => state.handleSupportModal);

  const currentTheme = useThemeStore(state => state.theme);
  const antdConfig = currentTheme === "dark" ? getDarkAntdTheme() : getLightAntdTheme();

  const userId = useAuthStore(state => state.user?.email);

  const {
    pushSupported,
    userConsent,
    onRequestPermission,
    isSubscribing,
    error: pushError,
  } = usePushNotifications(userId);

  useEffect(() => {
    if (ConfEnv.ENV !== "development" && authStatus === "authenticated" && pushSupported && userConsent === "default") {
      const timer = setTimeout(() => {
        setShowNotificationPrompt(true);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [authStatus, pushSupported, userConsent]);

  useEffect(() => {
    startSessionValidation(validateSession);

    return () => {
      stopSessionValidation();
    };
  }, []);

  useEffect(() => {
    initializeAmplitude(userId);
  }, [userId]);

  // Initialize native bridge for communication with React Native app
  useEffect(() => {
    initializeNativeBridge();
  }, []);

  // Meta Pixel tracking disabled - now handled by backend
  // useMetaPixel(ConfEnv.META_PIXEL_ID, {
  //   autoConfig: true,
  //   debug: ConfEnv.ENV === "development",
  // });

  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [installDeviceType, setInstallDeviceType] = useState(null);

  useEffect(() => {
    const handleBeforeInstallPrompt = e => {
      e.preventDefault();
      setInstallPromptEvent(e);

      const userAgent = navigator.userAgent.toLowerCase();
      if (userAgent.includes("android")) {
        setInstallDeviceType("android");
      } else if (userAgent.includes("iphone") || userAgent.includes("ipad") || userAgent.includes("ipod")) {
        setInstallDeviceType("ios");
      } else {
        setInstallDeviceType("desktop");
      }

      // Don't show install prompt on Payku callback page
      if (window.location.pathname === "/payku/callback") return;

      // Don't show if user dismissed recently (7 days)
      const dismissed = localStorage.getItem("pwa_install_dismissed");
      if (dismissed && Date.now() - Number(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

      setShowInstallPrompt(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  return (
    <AppContext.Provider value={null}>
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <DynamicTitleProvider>
            <ConfigLoader />
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
                v7_fetcherPersist: true,
                v7_normalizeFormMethod: true,
                v7_partialHydration: true,
                v7_skipActionErrorRevalidation: true,
              }}
            >
              <ScrollToTop />
              <ConfigProvider
                theme={antdConfig}
                locale={locale}
              >
                <SupportModal
                  open={userSupportModaVisible}
                  onClose={handleSupportModal}
                />

                <Suspense fallback={<div>Loading...</div>}>
                  <ConditionalLayout>
                    <Routes>
                      <Route
                        path="/"
                        element={<ProtectedHomePage />}
                      />
                      {import.meta.env.DEV && ImageWithLoadSpinnerDevPreview != null && (
                        <Route
                          path="/dev/image-load-spinner"
                          element={<ImageWithLoadSpinnerDevPreview />}
                        />
                      )}
                      <Route
                        path="/mobile-login"
                        element={<MobileLogin />}
                      />
                      <Route
                        path="/mobile-registration"
                        element={<MobileRegistration />}
                      />
                      <Route
                        path="/terminos-y-condiciones"
                        element={<TermsOfService />}
                      />
                      <Route
                        path="/eliminar-cuenta"
                        element={<DeleteAccount />}
                      />
                      <Route
                        path="/payku/callback"
                        element={<PaykuCallback />}
                      />
                      {/* <Route path="/support" element={<Support />} /> */}
                      {/* Protected Routes - Wrapped with ProtectedRoute component */}
                      <Route
                        path="/games/category/:category"
                        element={
                          <ErrorBoundaryRedirect>
                            <GamesList />
                          </ErrorBoundaryRedirect>
                        }
                      />
                      <Route
                        path="/games/provider/:provider"
                        element={
                          <ErrorBoundaryRedirect>
                            <GamesList />
                          </ErrorBoundaryRedirect>
                        }
                      />
                      <Route
                        path="/gameslist/all"
                        element={
                          <ErrorBoundaryRedirect>
                            <GamesList />
                          </ErrorBoundaryRedirect>
                        }
                      />
                      <Route
                        path="/games/:provider/:gameId"
                        element={
                          <ErrorBoundaryRedirect>
                            <GamingScreen />
                          </ErrorBoundaryRedirect>
                        }
                      />
                      <Route
                        path="/lottery"
                        element={
                          <ErrorBoundaryRedirect>
                            <Lottery />
                          </ErrorBoundaryRedirect>
                        }
                      />
                      <Route
                        path="/lottery/:category"
                        element={
                          <ErrorBoundaryRedirect>
                            <Lottery />
                          </ErrorBoundaryRedirect>
                        }
                      />
                      <Route
                        path="/lottery/:gameIdString"
                        element={
                          <ErrorBoundaryRedirect>
                            <LotteryGameRoute />
                          </ErrorBoundaryRedirect>
                        }
                      />
                      <Route
                        path="/lottery/:gameId/results"
                        element={<Results />}
                      />
                      <Route
                        path="/lottery/tickets"
                        exact
                        element={<MyTickets />}
                      />
                      <Route
                        path="/raffles"
                        element={<RafflesList />}
                      />
                      <Route
                        path="/raffles/tickets"
                        element={<RaffleTickets />}
                      />
                      <Route
                        path="/live-casino"
                        element={<LiveCasino />}
                      />
                      <Route
                        path="/bingo"
                        element={
                          <ErrorBoundaryRedirect>
                            <Bingo />
                          </ErrorBoundaryRedirect>
                        }
                      />
                      <Route
                        path="/horses"
                        element={
                          <ErrorBoundaryRedirect>
                            <Horses />
                          </ErrorBoundaryRedirect>
                        }
                      />
                      {/* <Route path="/hipism" element={<Hipism />} /> */}
                      <Route
                        path="/news"
                        element={
                          <ErrorBoundaryRedirect>
                            <News />
                          </ErrorBoundaryRedirect>
                        }
                      />
                      <Route
                        path="/article/:slug"
                        element={
                          <ErrorBoundaryRedirect>
                            <Article />
                          </ErrorBoundaryRedirect>
                        }
                      />
                      <Route
                        path="/testimonios"
                        element={
                          <ErrorBoundaryRedirect>
                            <Testimonials />
                          </ErrorBoundaryRedirect>
                        }
                      />
                      <Route
                        path="/game-feed"
                        element={
                          <ErrorBoundaryRedirect>
                            <GameFeed />
                          </ErrorBoundaryRedirect>
                        }
                      />
                      <Route
                        path="/provider/ayg"
                        element={
                          <ErrorBoundaryRedirect>
                            <AYGProfile />
                          </ErrorBoundaryRedirect>
                        }
                      />
                      <Route
                        path="/provider/:provider"
                        element={
                          <ErrorBoundaryRedirect>
                            <ProviderProfile />
                          </ErrorBoundaryRedirect>
                        }
                      />
                      <Route
                        path="/mini-juegos"
                        element={
                          <ErrorBoundaryRedirect>
                            <MiniJuegos />
                          </ErrorBoundaryRedirect>
                        }
                      />
                      <Route
                        path="/casino"
                        element={
                          <ErrorBoundaryRedirect>
                            <CasinoList />
                          </ErrorBoundaryRedirect>
                        }
                      />
                      <Route
                        path="/sports"
                        element={
                          <ErrorBoundaryRedirect>
                            <Parley />
                          </ErrorBoundaryRedirect>
                        }
                      />
                      <Route
                        path="/user/contacts"
                        element={<ContactsNavbar />}
                      />
                      <Route
                        path="/user/pago-movil"
                        element={<PagoMovil />}
                      />
                      <Route
                        path="/user/transactions"
                        element={<Movements />}
                      />
                      {/* Lotoven routes - only for authorized users */}
                      <Route
                        path="/lotoven"
                        element={<Lotoven />}
                      />
                      <Route
                        path="/lotoven/home"
                        element={<Lotoven />}
                      />
                      <Route
                        path="/lotoven/animalitos"
                        element={<Lotoven />}
                      />
                      <Route
                        path="/lotoven/loterias"
                        element={<Lotoven />}
                      />
                      <Route
                        path="/user/referrals"
                        element={<ReferralProgram />}
                      />
                      <Route
                        path="/settings"
                        element={<AccountSettingsPage />}
                      />
                      <Route
                        path="/favorites"
                        element={
                          <ErrorBoundaryRedirect>
                            <FavoritesPage />
                          </ErrorBoundaryRedirect>
                        }
                      />
                      <Route
                        path="/u/:username"
                        element={<PublicProfilePage />}
                      />
                      {/* <Route path="/support" element={<Support />} /> */}
                      {/*     <Route path="/contacto" element={<Contact />} /> */}
                    </Routes>
                  </ConditionalLayout>
                </Suspense>
                <MobileBottomDock />

                <BaseModal
                  state={visible}
                  closeModal={handleShowAuthModal}
                  width={
                    authForm === "registration"
                      ? window.innerWidth <= 768
                        ? "90vw"
                        : "1200px"
                      : authForm === "recover"
                        ? "530px"
                        : window.innerWidth <= 768
                          ? "80vw"
                          : "950px"
                  }
                  className={authForm === "login" ? "auth-modal" : authForm === "registration" ? "register-modal" : ""}
                  wrapClassName={authForm === "login" ? "auth-modal" : ""}
                >
                  {authForm === "login" && <LoginForm />}
                  {authForm === "recover" && <RecoverPassword />}
                  {authForm === "registration" && <RegistrationModal />}
                </BaseModal>

                <SupportModal
                  open={userSupportModaVisible}
                  onClose={handleSupportModal}
                />

                <BaseModal
                  title=""
                  state={userProfileModaVisible}
                  closeModal={handleProfileModal}
                  width="500px"
                  className="profile-modal"
                >
                  <UserProfile />
                </BaseModal>

                <BaseModal
                  title=""
                  state={bankAccountModalVisible}
                  closeModal={handleBankAccountModal}
                  width={depositModalWidth}
                  destroyOnClose
                >
                  {bankAccountModalVisible &&
                    (depositModalType === "bankOptions" ? (
                      <BankOptions
                        prev={() => setDepositModalType("depositMethods")}
                        onWidthChange={setDepositModalWidth}
                      />
                    ) : (
                      <DepositMethods
                        next={() => setDepositModalType("bankOptions")}
                        onWidthChange={setDepositModalWidth}
                      />
                    ))}
                </BaseModal>

                <BaseModal
                  title={
                    <h4
                      style={{
                        fontWeight: 600,
                        textAlign: "center",
                      }}
                    >
                      {t("baseModalTitleChangePassword")}
                    </h4>
                  }
                  state={changePasswordModalVisible}
                  closeModal={handleChangePasswordModal}
                  width={420}
                >
                  <ChangePassword />
                </BaseModal>

                <BaseModal
                  title={
                    <h4
                      style={{
                        textAlign: "center",
                        fontSize: isMobile ? "18px" : "25px",
                        fontWeight: "700",
                      }}
                    >
                      {t("baseModalTitleWithdrawal")}
                    </h4>
                  }
                  state={withdrawalModalVisible}
                  closeModal={handleWithdrawalModal}
                  width={withdrawalModalWidth}
                >
                  <WithdrawalMethods
                    setWithdrawalModalWidth={setWithdrawalModalWidth}
                    closeModal={handleWithdrawalModal}
                  />
                </BaseModal>

                {/* Transfer functionality is temporarily disabled */}
                <BaseModal
                  title={
                    <h4
                      style={{
                        textAlign: "center",
                        fontSize: isMobile ? "18px" : "25px",
                        fontWeight: "700",
                      }}
                    >
                      {t("baseModalTitleTransfer")}
                    </h4>
                  }
                  state={transferModalVisible}
                  closeModal={handleTransferModal}
                  width="930px"
                >
                  <TransferSteps />
                </BaseModal>

                <BaseModal
                  title={t("mobilePaymentAdd")}
                  state={addPagoMovilModalVisible}
                  closeModal={handleAddPagoMovilModal}
                  width={420}
                >
                  <AddPagoMovilForm
                    onSuccess={handleAddPagoMovilModal}
                    onCancel={handleAddPagoMovilModal}
                  />
                </BaseModal>
                <BaseModal
                  state={showNotificationPrompt}
                  closeModal={() => setShowNotificationPrompt(false)}
                  className="notification-prompt-modal"
                  width="607px"
                  closable={false}
                >
                  <NotificationPrompt
                    onClose={() => setShowNotificationPrompt(false)}
                    onRequestPermission={() => {
                      onRequestPermission();
                      setShowNotificationPrompt(false);
                    }}
                    pushSupported={pushSupported}
                    userConsent={userConsent}
                    isSubscribing={isSubscribing}
                    error={pushError}
                  />
                </BaseModal>
                <BaseModal
                  state={showInstallPrompt}
                  closeModal={() => {
                    localStorage.setItem("pwa_install_dismissed", String(Date.now()));
                    setShowInstallPrompt(false);
                  }}
                >
                  <InstallationPrompt
                    deviceType={installDeviceType}
                    promptEvent={installPromptEvent}
                    onClose={() => {
                      localStorage.setItem("pwa_install_dismissed", String(Date.now()));
                      setShowInstallPrompt(false);
                    }}
                  />
                </BaseModal>

                {authStatus === "authenticated" && (
                  <>
                    <PagoMovilRegistrationModal />
                    <PagoMovilListModal />
                  </>
                )}

                <UpdatePrompt />

                {/* Push Notification Permission Prompt */}
              </ConfigProvider>
            </BrowserRouter>
            <ReactQueryDevtools />
          </DynamicTitleProvider>
        </HelmetProvider>
      </QueryClientProvider>
    </AppContext.Provider>
  );
};

export default App;
